import React, { useState, useEffect } from "react";
import styles from "./Auth.module.scss";
import { voleeApi } from "../../api/VoleeApi";
import { useCommunity, useLogin } from "../../context/community";
import { NavLink, Link } from "react-router-dom";
import { useHistory, useLocation } from "react-router-dom";
import { PAGES } from "../../routes/PAGES";
import { AUTH_TYPES } from "../../context/AUTH_TYPES";
import LangService from "../../services/LangService";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import validation from "./validation";
import Checkbox from "components/Checkbox/Checkbox";
import OAuthLogin from "./components/OAuthLogin";
import { useGoogleLogin } from "./GoogleLogin/useGoogleLogin";
import { GoogleLogin } from "./GoogleLogin/GoogleLogin";
import { useOneTimeCode } from "./OneTimeCode/useOneTimeCode";
import { useServerProperties } from "services/server-properties";
import { ShortAuth } from "./ShortAuth/ShortAuth";
import cn from "classnames";
import { resolveCommunityLogo } from "services/themePalette";

const Registration = () => {
    const GTAG_EVENT = 'sign_up'
    const login = useLogin();
    const history = useHistory();
    const { docs, consentConfig, preLoginConsents } = useServerProperties();
    const { state, search } = useLocation();
    const { email: emailPassed } = state || {};
    const [email, setEmail] = useState(emailPassed || "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errorText, setErrorText] = useState("");
    const [disabled, setDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [redirectTo, setRedirectTo] = useState(null);
    const [consentCheckedMap, setConsentCheckedMap] = useState({});
    const { community: { domain, authConfig, logoUrl, darkLogoUrl }, themeMode } = useCommunity();
    const brandLogo = resolveCommunityLogo({ mode: themeMode, logoUrl, darkLogoUrl });
    const { hasCodeAuth } = useOneTimeCode();
    const hasEmail = authConfig?.findIndex(conf => conf.authType === AUTH_TYPES.EMAIL) !== -1;
    const hasOAuth2 = authConfig && authConfig.findIndex(conf => conf.authType === "OAUTH2") !== -1;
    const authMap = LangService.map.pages.Auth;
    const langMap = LangService.map.pages.Registration;
    const checkboxId = "pp-checkbox";
    const hasCustomPreLoginConsents = preLoginConsents.length > 0;

    const isPreLoginConsentAccepted = () => {
        if (!hasCustomPreLoginConsents) {
            return !disabled;
        }

        return preLoginConsents
            .filter((consent) => consent.required)
            .every((consent) => Boolean(consentCheckedMap[consent.id]));
    }

    // Read language parameter from URL and set it
    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const langParam = queryParams.get('lang');
        if (langParam) {
            // Convert to uppercase (RU/EN) as LangService expects
            const langUpper = langParam.toUpperCase();
            if (langUpper === 'RU' || langUpper === 'EN') {
                LangService.lang = langUpper;
            }
        }
    }, [search]);

    useEffect(() => {
        const intendedPath = sessionStorage.getItem('redirectToAfterLogin');
        if (intendedPath) {
            setRedirectTo(intendedPath);
        }
    }, []);

    const handleLoginError = (errorText) => {
        console.warn("[Login failed] res:", errorText);
        history.push(PAGES.LOGIN)
    }

    const { hasGoogle, handleGoogleSuccess, handleGoogleFailure } = useGoogleLogin({
        gtagEvent: GTAG_EVENT,
        setErrorText: handleLoginError
    })

    const handleError = () => {
        setErrorText("");
        const error =
            validation.email(email) ||
            validation.password(password) ||
            validation.confirmPassword(password, confirmPassword);

        if (error) {
            setErrorText(error);
        }

        return Boolean(error);
    };

    const loginSubmit = async () => {
        try {
            const authContext = {
                authType: AUTH_TYPES.EMAIL,
                email: email,
                password: password,
                token: null,
                backTo: redirectTo,
                introFor: null
            }
            await login(authContext);
            // No need to remove redirectTo from session storage here,
            // as the login function in community.js will handle the redirect,
            // and login.jsx will clear it if this was the entry point.
            // If registration is successful, the login function takes over.
        } catch (e) {
            handleLoginError(e.msg);
        }
    };

    const handleSubmit = async () => {
        if (!isPreLoginConsentAccepted() || handleError()) {
            return;
        }
        setDisabled(true);
        setLoading(true);
        try {
            const result = await voleeApi.auth.registerUser({ email, password });
            if (result === "OK") {
                gtag("event", GTAG_EVENT, {
                    method: "Email",
                });
                await loginSubmit();
                // If loginSubmit is successful, redirection will happen.
                // If it fails, an error is shown, and the user stays on the page.
                // The redirectTo value will persist for the next attempt.
                // Clearing sessionStorage should ideally happen after successful redirection.
                // For now, we rely on Login.jsx or the next successful auth to clear it.
                if (redirectTo && result === "OK") { // Clear only if registration and subsequent login were fine
                    sessionStorage.removeItem('redirectToAfterLogin');
                }
                setDisabled(false)
            }
        } catch (error) {
            setErrorText(langMap?.error[error.msg] || error.msg);
            setDisabled(false);
        }
        setLoading(false);
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === 13) {
            return handleSubmit();
        }
    };

    const handleCheckbox = async (checked) => {
        setDisabled(!checked)
    }

    const handleConsentCheckbox = (consentId, checked) => {
        setConsentCheckedMap((prev) => ({
            ...prev,
            [consentId]: checked
        }));
    }

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={styles.brandLogo}>
                    <NavLink to={PAGES.WELCOME}>
                        <img src={brandLogo} alt="brand" width="200" />
                    </NavLink>
                </div>
                <div className={styles.loginText}>{langMap.title}</div>
                <div className={styles.loginSubText}>{domain !== 'nw' ? (langMap.subtitle) : 'Создайте свой профиль в сообществе банка "Ренессанс Кредит"'}</div>
                {hasGoogle && !hasCodeAuth && (
                    <GoogleLogin
                        onGoogleSuccess={handleGoogleSuccess}
                        onGoogleFailure={handleGoogleFailure}
                    />
                )}
                {hasCodeAuth && (
                    <ShortAuth gtagEvent={GTAG_EVENT} />
                )}
                {hasOAuth2 && <OAuthLogin config={authConfig} />}
                {errorText && <div className={styles.errorText} dangerouslySetInnerHTML={{ __html: errorText }}></div>}
                {hasEmail && <>
                    {authConfig?.length > 1 &&
                        <div className={styles.orContinueWith}>
                            <span>{authMap.orContinueWith}</span>
                        </div>}
                    <Input
                        label={authMap.email}
                        className={styles.input}
                        type="email"
                        value={email}
                        onChange={setEmail}
                        onKeyDown={handleKeyDown}
                        name="email"
                        autoComplete="email"
                        required
                        autoFocus={!emailPassed}
                    />
                    <Input
                        label={authMap.password}
                        className={styles.input}
                        type="password"
                        value={password}
                        onChange={setPassword}
                        onKeyDown={handleKeyDown}
                        name="password"
                        placeholder={authMap.passwordHint}
                        autoComplete="new-password"
                        required
                        autoFocus={emailPassed}
                    />
                    <Input
                        label={authMap.repeatedPassword}
                        className={styles.input}
                        type="password"
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        onKeyDown={handleKeyDown}
                        name="confirm_password"
                        autoComplete="new-password"
                        required
                    />
                    <div className={styles.bottomQuestion}>
                        {hasCustomPreLoginConsents ? (
                            preLoginConsents.map((consent) => {
                                const consentCheckboxId = `${checkboxId}-${consent.id}`;
                                return (
                                    <div className={styles.privacyCheckbox} key={consent.id}>
                                        <Checkbox
                                            id={consentCheckboxId}
                                            onChange={(checked) => handleConsentCheckbox(consent.id, checked)}
                                            className={styles.checkbox}
                                        />
                                        <label htmlFor={consentCheckboxId} className={styles.privacyLabel}>
                                            <span dangerouslySetInnerHTML={{ __html: consent.text }} />
                                        </label>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.privacyCheckbox}>
                                <Checkbox id={checkboxId} onChange={handleCheckbox} className={styles.checkbox} />
                                <label htmlFor={checkboxId} className={styles.privacyLabel}>
                                    {consentConfig?.enabled ? (
                                        <span dangerouslySetInnerHTML={{ __html: consentConfig.text }} />
                                    ) : (
                                        <>
                                            {langMap.agreePolicy[0]}
                                            {domain === 'multicarta' || domain === 'ciao' ?
                                                <>{langMap.termsOfUse}</>
                                                :
                                                <a
                                                    href={domain === 'nw' ?
                                                        "/community/nw/assets/User_Agreement.pdf" :
                                                        docs.termsUrl
                                                    }
                                                    target="_blank"
                                                >
                                                    {langMap.termsOfUse}
                                                </a>
                                            }

                                            {langMap.agreePolicy[1]}
                                            {domain === 'multicarta' || domain === 'ciao' ?
                                                <>{langMap.privacyPolicy}</>
                                                :
                                                <a
                                                    href={domain === 'nw' ?
                                                        "/community/nw/assets/Policy.pdf" :
                                                        domain === 'annamoney' ?
                                                            PAGES.PRIVATE_POLICY_ANNAMONEY :
                                                            docs.policyUrl
                                                    }
                                                    target="_blank"
                                                >
                                                    {langMap.privacyPolicy}
                                                </a>
                                            }
                                        </>
                                    )}
                                </label>
                            </div>
                        )}
                    </div>
                    {domain === 'nw' ? (
                        <Button
                            wide
                            type="button"
                            onClick={handleSubmit}
                            style={styles.submitButton}
                            disabled={!isPreLoginConsentAccepted()}
                        >
                            {langMap.button}
                        </Button>
                    ) : (
                        <Button
                            wide
                            type="button"
                            onClick={handleSubmit}
                            style={styles.submitButton}
                            active
                            disabled={!isPreLoginConsentAccepted()}
                            loading={loading}
                        >
                            {langMap.button}
                        </Button>
                    )}
                </>}
                <p className={cn(styles.bottomQuestion, {
                    [styles.shortAuth]: hasCodeAuth
                })}>
                    {langMap.accountQuestion} <Link className={styles.link} to={PAGES.LOGIN}>{langMap.link}</Link>
                </p>
            </div>
        </div>
    );
};

export default Registration;
